<template>
  <div>
    <div class="page-header">
      <div>
        <h5 class="page-title">Visitor Directory</h5>
        <p class="page-sub">All registered visitor profiles</p>
      </div>
    </div>

    <!-- Filters -->
    <div class="filter-bar">
      <input v-model="search" class="filter-input" placeholder="Search name, email, phone, company…" style="min-width:240px;" @input="debounceLoad" />
      <select v-model="filterBlocked" class="filter-select" @change="load()">
        <option value="">All Visitors</option>
        <option value="0">Active</option>
        <option value="1">Blocked</option>
      </select>
      <div class="per-page-wrap">
        <label class="per-page-label">Show</label>
        <select class="per-page-select" v-model.number="perPage" @change="load(1)">
          <option v-for="n in [10,20,50,100]" :key="n" :value="n">{{ n }}</option>
        </select>
        <span class="per-page-label">per page</span>
      </div>
    </div>

    <!-- Skeleton -->
    <div v-if="loading" class="sk-table">
      <div v-for="n in 6" :key="n" class="sk-row"></div>
    </div>

    <!-- Empty -->
    <div v-else-if="!visitors.length" class="empty-state">
      <font-awesome-icon icon="users" class="empty-icon" />
      <p>No visitor profiles found.</p>
    </div>

    <!-- Table -->
    <div v-else class="table-card">
      <table class="data-table">
        <thead>
          <tr>
            <th>Visitor</th>
            <th>Company</th>
            <th>Phone</th>
            <th>Visits</th>
            <th>Status</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="v in visitors" :key="v.id" :class="{ 'row--blocked': v.is_blocked }">
            <td>
              <div class="vis-cell">
                <div class="vis-avatar" :class="{ 'vis-avatar--blocked': v.is_blocked }">
                  <img v-if="v.photo_url" :src="v.photo_url" class="vis-avatar-img" alt="" />
                  <span v-else>{{ initials(v) }}</span>
                </div>
                <div>
                  <div class="vis-name">{{ v.first_name }} {{ v.last_name }}</div>
                  <div class="vis-sub">{{ v.email }}</div>
                </div>
              </div>
            </td>
            <td>{{ v.company || '—' }}</td>
            <td>{{ v.phone || '—' }}</td>
            <td>
              <span class="chip chip--blue">{{ v.visits_count ?? '—' }}</span>
            </td>
            <td>
              <span v-if="v.is_blocked" class="chip chip--red">Blocked</span>
              <span v-else-if="v.flagged" class="chip chip--amber">Flagged</span>
              <span v-else class="chip chip--green">Active</span>
            </td>
            <td>
              <div class="row-menu-wrap">
                <button class="row-menu-trigger" @click.stop="toggleRowMenu(v.id, $event)">
                  <font-awesome-icon icon="ellipsis-v" />
                </button>
                <transition name="menu-pop">
                  <div v-if="openMenuId === v.id" class="row-menu-dropdown"
                       :style="{ top: menuPos.top + 'px', right: menuPos.right + 'px' }"
                       @click.stop>
                    <button class="row-menu-item" @click="openEdit(v); openMenuId = null">
                      <font-awesome-icon icon="edit" class="row-menu-icon" /> View / Edit
                    </button>
                    <button class="row-menu-item" @click="openCredentials(v); openMenuId = null">
                      <font-awesome-icon icon="id-card" class="row-menu-icon" /> Credentials
                    </button>
                    <div class="row-menu-divider"></div>
                    <button v-if="!v.is_blocked" class="row-menu-item row-menu-item--red" @click="promptBlock(v); openMenuId = null">
                      <font-awesome-icon icon="ban" class="row-menu-icon" /> Block Visitor
                    </button>
                    <button v-else class="row-menu-item row-menu-item--green" @click="unblock(v); openMenuId = null">
                      <font-awesome-icon icon="check-circle" class="row-menu-icon" /> Unblock Visitor
                    </button>
                    <div class="row-menu-divider"></div>
                    <button class="row-menu-item row-menu-item--red" @click="promptDelete(v); openMenuId = null">
                      <font-awesome-icon icon="trash" class="row-menu-icon" /> Delete Profile
                    </button>
                  </div>
                </transition>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <div v-if="pagination.last_page >= 1" class="pagination-bar">
        <span class="pg-info">
          Showing <strong>{{ pagination.from }}</strong>–<strong>{{ pagination.to }}</strong> of <strong>{{ pagination.total }}</strong>
        </span>
        <div class="pg-controls">
          <button class="pg-btn" :disabled="pagination.current_page===1" @click="load(1)">«</button>
          <button class="pg-btn" :disabled="pagination.current_page===1" @click="load(pagination.current_page-1)">‹</button>
          <button
            v-for="p in pageRange" :key="p"
            class="pg-num" :class="p===pagination.current_page ? 'pg-num--active' : ''"
            @click="load(p)">{{ p }}</button>
          <button class="pg-btn" :disabled="pagination.current_page===pagination.last_page" @click="load(pagination.current_page+1)">›</button>
          <button class="pg-btn" :disabled="pagination.current_page===pagination.last_page" @click="load(pagination.last_page)">»</button>
        </div>
      </div>
    </div>

    <!-- ── Edit modal ───────────────────────────────────────────────────── -->
    <div v-if="editVisitor" class="modal-overlay" @click.self="editVisitor = null">
      <div class="modal-box">
        <div class="modal-head">
          <h5 class="modal-title">Edit Visitor Profile</h5>
          <button class="modal-close" @click="editVisitor = null"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body">
          <div class="form-cols">
            <div class="form-row">
              <label class="form-label">First Name</label>
              <input v-model="editForm.first_name" class="form-input" />
            </div>
            <div class="form-row">
              <label class="form-label">Last Name</label>
              <input v-model="editForm.last_name" class="form-input" />
            </div>
          </div>
          <div class="form-cols" style="margin-top:12px;">
            <div class="form-row">
              <label class="form-label">Email</label>
              <input v-model="editForm.email" type="email" class="form-input" />
            </div>
            <div class="form-row">
              <label class="form-label">Phone</label>
              <input v-model="editForm.phone" class="form-input" />
            </div>
          </div>
          <div class="form-row" style="margin-top:12px;">
            <label class="form-label">Company</label>
            <input v-model="editForm.company" class="form-input" />
          </div>
          <div class="form-row" style="margin-top:12px;">
            <label class="form-label">Notes</label>
            <textarea v-model="editForm.notes" class="form-input" rows="2" style="resize:vertical;line-height:1.5;"></textarea>
          </div>
          <div class="setting-row" style="margin-top:16px;">
            <div class="toggle-info">
              <span class="toggle-name">Flagged for review</span>
              <span class="toggle-hint">Marks the visitor without blocking them</span>
            </div>
            <label class="toggle-switch">
              <input type="checkbox" v-model="editForm.flagged" />
              <span class="toggle-track"></span>
            </label>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-ghost" @click="editVisitor = null">Cancel</button>
          <button class="btn-primary-action" @click="saveEdit" :disabled="saving">
            <font-awesome-icon v-if="saving" icon="spinner" spin />
            {{ saving ? 'Saving…' : 'Save Changes' }}
          </button>
        </div>
      </div>
    </div>

    <!-- ── Block modal ──────────────────────────────────────────────────── -->
    <div v-if="blockTarget" class="modal-overlay" @click.self="blockTarget = null">
      <div class="modal-box" style="max-width:420px;">
        <div class="modal-head">
          <h5 class="modal-title">Block Visitor</h5>
          <button class="modal-close" @click="blockTarget = null"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body">
          <p style="font-size:13px;color:#4a5e78;margin:0 0 12px;">
            Block <strong>{{ blockTarget.first_name }} {{ blockTarget.last_name }}</strong> from checking in. They will be refused entry if they attempt to walk in or use the kiosk.
          </p>
          <div class="form-row">
            <label class="form-label">Reason (optional)</label>
            <input v-model="blockReason" class="form-input" placeholder="e.g. Trespassing, security concern…" />
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-ghost" @click="blockTarget = null">Cancel</button>
          <button class="btn-danger" @click="confirmBlock" :disabled="blocking">
            <font-awesome-icon v-if="blocking" icon="spinner" spin />
            {{ blocking ? 'Blocking…' : 'Block Visitor' }}
          </button>
        </div>
      </div>
    </div>

    <!-- ── Delete modal ─────────────────────────────────────────────────── -->
    <div v-if="deleteTarget" class="modal-overlay" @click.self="deleteTarget = null">
      <div class="modal-box" style="max-width:400px;">
        <div class="modal-head">
          <h5 class="modal-title">Delete Visitor Profile</h5>
          <button class="modal-close" @click="deleteTarget = null"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body">
          <p style="font-size:13px;color:#4a5e78;margin:0;">
            Permanently delete <strong>{{ deleteTarget.first_name }} {{ deleteTarget.last_name }}</strong>'s profile? This cannot be undone. Visit history will be retained.
          </p>
        </div>
        <div class="modal-footer">
          <button class="btn-ghost" @click="deleteTarget = null">Cancel</button>
          <button class="btn-danger" @click="confirmDelete" :disabled="deleting">
            <font-awesome-icon v-if="deleting" icon="spinner" spin />
            {{ deleting ? 'Deleting…' : 'Delete Profile' }}
          </button>
        </div>
      </div>
    </div>

    <!-- ── Credentials modal ───────────────────────────────────────────── -->
    <div v-if="credVisitor" class="modal-overlay" @click.self="closeCredentials">
      <div class="modal-box" style="max-width:620px;">
        <div class="modal-head">
          <h5 class="modal-title">
            Credentials — {{ credVisitor.first_name }} {{ credVisitor.last_name }}
          </h5>
          <button class="modal-close" @click="closeCredentials"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body" style="max-height:70vh;overflow-y:auto;">
          <!-- Loading -->
          <div v-if="credLoading" style="text-align:center;padding:30px;color:#7a90b0;">
            <font-awesome-icon icon="spinner" spin /> Loading…
          </div>

          <!-- List -->
          <div v-else>
            <div v-if="credentials.length" class="cred-list">
              <div v-for="c in credentials" :key="c.id" class="cred-item" :class="{ 'cred-item--expired': c.is_expired }">
                <div class="cred-item-info">
                  <div class="cred-type-badge">{{ credTypeLabel(c.type) }}</div>
                  <div class="cred-number">{{ c.credential_number }}</div>
                  <div v-if="c.issuing_country" class="cred-meta">{{ c.issuing_country }}</div>
                  <div v-if="c.expiry_date" class="cred-meta" :class="{ 'cred-expired-text': c.is_expired }">
                    {{ c.is_expired ? 'Expired' : 'Expires' }}: {{ c.expiry_date }}
                  </div>
                  <div v-if="c.notes" class="cred-meta cred-notes">{{ c.notes }}</div>
                </div>
                <button class="btn-icon btn-icon--red" @click="deleteCred(c)" title="Remove credential">
                  <font-awesome-icon icon="trash" />
                </button>
              </div>
            </div>
            <div v-else style="text-align:center;padding:20px;font-size:13px;color:#aab8cc;">No credentials on file.</div>

            <!-- Add form -->
            <div class="cred-add-section">
              <div class="section-label" style="font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.05em;color:#7a90b0;margin-bottom:12px;">Add Credential</div>
              <div class="cred-form-grid">
                <div class="form-row">
                  <label class="form-label">Type <span class="req">*</span></label>
                  <select v-model="credForm.type" class="form-input">
                    <option value="">— Select —</option>
                    <option value="passport">Passport</option>
                    <option value="national_id">National ID</option>
                    <option value="driving_license">Driving Licence</option>
                    <option value="work_permit">Work Permit</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div class="form-row">
                  <label class="form-label">Credential Number <span class="req">*</span></label>
                  <input v-model="credForm.credential_number" class="form-input" placeholder="e.g. AB1234567" />
                </div>
                <div class="form-row">
                  <label class="form-label">Issuing Country</label>
                  <div class="cs-wrap" ref="countryWrap">
                    <div class="cs-trigger" :class="{ 'cs-trigger--open': showCountryDrop }" @click="openCountryDrop">
                      <span v-if="credForm.issuing_country" class="cs-selected">
                        <span class="cs-flag">{{ countryFlag(credForm.issuing_country) }}</span>
                        {{ countryName(credForm.issuing_country) }}
                      </span>
                      <span v-else class="cs-placeholder">— Select country —</span>
                      <font-awesome-icon :icon="showCountryDrop ? 'chevron-up' : 'chevron-down'" class="cs-chevron" />
                    </div>
                    <div v-if="showCountryDrop" class="cs-dropdown">
                      <div class="cs-search-wrap">
                        <font-awesome-icon icon="search" class="cs-search-icon" />
                        <input
                          ref="countrySearch"
                          v-model="countryQuery"
                          class="cs-search-input"
                          placeholder="Search country…"
                          @click.stop
                        />
                        <button v-if="countryQuery" class="cs-clear" @click.stop="countryQuery = ''">
                          <font-awesome-icon icon="times" />
                        </button>
                      </div>
                      <ul class="cs-list">
                        <li
                          v-for="c in filteredCountries"
                          :key="c.sortname"
                          class="cs-item"
                          :class="{ 'cs-item--active': credForm.issuing_country === c.sortname }"
                          @click.stop="selectCountry(c)"
                        >
                          <span class="cs-flag">{{ countryFlag(c.sortname) }}</span>
                          <span class="cs-name">{{ c.name }}</span>
                          <span class="cs-code">{{ c.sortname }}</span>
                        </li>
                        <li v-if="!filteredCountries.length" class="cs-empty">No countries found</li>
                      </ul>
                      <div v-if="credForm.issuing_country" class="cs-footer">
                        <button class="cs-clear-btn" @click.stop="credForm.issuing_country = ''; showCountryDrop = false">
                          Clear selection
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="form-row">
                  <label class="form-label">Expiry Date</label>
                  <input v-model="credForm.expiry_date" type="date" class="form-input" />
                </div>
              </div>
              <div class="form-row" style="margin-top:10px;">
                <label class="form-label">Notes</label>
                <input v-model="credForm.notes" class="form-input" placeholder="Optional notes…" />
              </div>
              <div style="display:flex;justify-content:flex-end;margin-top:12px;">
                <button class="btn-primary-action" @click="saveCred" :disabled="credSaving">
                  <font-awesome-icon v-if="credSaving" icon="spinner" spin />
                  {{ credSaving ? 'Saving…' : 'Add Credential' }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import axios from 'axios'
import { useToast } from 'vue-toastification'

export default {
  name: 'VisitorDirectory',
  setup() { return { toast: useToast() } },
  data() {
    return {
      visitors: [], loading: true,
      search: '', filterBlocked: '', perPage: 20,
      pagination: { current_page: 1, last_page: 1, total: 0, from: 0, to: 0 },
      debounceTimer: null,
      editVisitor: null, editForm: {}, saving: false,
      blockTarget: null, blockReason: '', blocking: false,
      deleteTarget: null, deleting: false,
      credVisitor: null, credentials: [], credLoading: false, credSaving: false,
      credForm: { type: '', credential_number: '', issuing_country: '', expiry_date: '', notes: '' },
      openMenuId: null, menuPos: { top: 0, right: 0 },
      countries: [], countryQuery: '', showCountryDrop: false,
    }
  },
  computed: {
    filteredCountries() {
      const q = this.countryQuery.trim().toLowerCase()
      if (!q) return this.countries
      return this.countries.filter(c =>
        c.name.toLowerCase().includes(q) || c.sortname.toLowerCase().includes(q)
      )
    },
    pageRange() {
      const cur = this.pagination.current_page, last = this.pagination.last_page
      const range = []
      for (let i = Math.max(1, cur - 2); i <= Math.min(last, cur + 2); i++) range.push(i)
      return range
    },
  },
  created() {
    this.load()
    this.loadCountries()
  },
  mounted() {
    this._menuClose = () => { this.openMenuId = null }
    this._countryClose = (e) => {
      if (this.$refs.countryWrap && !this.$refs.countryWrap.contains(e.target)) {
        this.showCountryDrop = false
      }
    }
    document.addEventListener('click', this._menuClose)
    document.addEventListener('scroll', this._menuClose, true)
    document.addEventListener('click', this._countryClose)
  },
  beforeUnmount() {
    document.removeEventListener('click', this._menuClose)
    document.removeEventListener('scroll', this._menuClose, true)
    document.removeEventListener('click', this._countryClose)
  },
  methods: {
    async loadCountries() {
      try {
        const r = await axios.get('countries')
        // ResponseHelper wraps the repo response: r.data = { data: { data: [...], status: true } }
        const raw = r.data?.data?.data ?? r.data?.data ?? r.data ?? []
        this.countries = (Array.isArray(raw) ? raw : Object.values(raw))
          .filter(c => c.status !== 0)
          .sort((a, b) => a.name.localeCompare(b.name))
      } catch { /* non-critical */ }
    },
    openCountryDrop() {
      this.showCountryDrop = !this.showCountryDrop
      if (this.showCountryDrop) {
        this.countryQuery = ''
        this.$nextTick(() => this.$refs.countrySearch?.focus())
      }
    },
    selectCountry(c) {
      this.credForm.issuing_country = c.sortname
      this.showCountryDrop = false
      this.countryQuery = ''
    },
    countryName(code) {
      return this.countries.find(c => c.sortname === code)?.name ?? code
    },
    countryFlag(code) {
      if (!code || code.length !== 2) return ''
      return String.fromCodePoint(...[...code.toUpperCase()].map(ch => 0x1F1E6 + ch.charCodeAt(0) - 65))
    },
    toggleRowMenu(id, event) {
      if (this.openMenuId === id) { this.openMenuId = null; return }
      const rect = event.currentTarget.getBoundingClientRect()
      this.menuPos = { top: rect.bottom + 4, right: window.innerWidth - rect.right }
      this.openMenuId = id
    },
    async load(page = 1) {
      this.loading = true
      try {
        const params = { page, per_page: this.perPage }
        if (this.search)        params.search  = this.search
        if (this.filterBlocked !== '') params.is_blocked = this.filterBlocked
        const r = await axios.get('visitors/profiles', { params })
        const d = r.data.data
        this.visitors   = d.data ?? d
        this.pagination = {
          current_page: d.current_page ?? 1,
          last_page:    d.last_page    ?? 1,
          total:        d.total        ?? 0,
          from:         d.from         ?? 0,
          to:           d.to           ?? 0,
        }
      } catch { this.toast.error('Failed to load visitor directory.') }
      finally   { this.loading = false }
    },
    debounceLoad() {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = setTimeout(() => this.load(), 400)
    },
    initials(v) {
      return ((v?.first_name?.[0] ?? '') + (v?.last_name?.[0] ?? '')).toUpperCase()
    },
    openEdit(v) {
      this.editVisitor = v
      this.editForm = {
        first_name: v.first_name,
        last_name:  v.last_name,
        email:      v.email      ?? '',
        phone:      v.phone      ?? '',
        company:    v.company    ?? '',
        notes:      v.notes      ?? '',
        flagged:    v.flagged    ?? false,
      }
    },
    async saveEdit() {
      this.saving = true
      try {
        await axios.put(`visitors/profiles/${this.editVisitor.id}`, this.editForm)
        this.toast.success('Profile updated.')
        this.editVisitor = null
        this.load(this.pagination.current_page)
      } catch (e) {
        this.toast.error(e.response?.data?.message ?? 'Failed to save.')
      } finally { this.saving = false }
    },
    promptBlock(v)   { this.blockTarget = v; this.blockReason = '' },
    async confirmBlock() {
      this.blocking = true
      try {
        await axios.post(`visitors/profiles/${this.blockTarget.id}/block`, { reason: this.blockReason })
        this.toast.success(`${this.blockTarget.first_name} ${this.blockTarget.last_name} has been blocked.`)
        this.blockTarget = null
        this.load(this.pagination.current_page)
      } catch (e) {
        this.toast.error(e.response?.data?.message ?? 'Failed to block visitor.')
      } finally { this.blocking = false }
    },
    async unblock(v) {
      try {
        await axios.post(`visitors/profiles/${v.id}/unblock`)
        this.toast.success(`${v.first_name} ${v.last_name} has been unblocked.`)
        this.load(this.pagination.current_page)
      } catch (e) {
        this.toast.error(e.response?.data?.message ?? 'Failed to unblock.')
      }
    },
    promptDelete(v) { this.deleteTarget = v },
    async confirmDelete() {
      this.deleting = true
      try {
        await axios.delete(`visitors/profiles/${this.deleteTarget.id}`)
        this.toast.success('Visitor profile deleted.')
        this.deleteTarget = null
        this.load(this.pagination.current_page)
      } catch (e) {
        this.toast.error(e.response?.data?.message ?? 'Failed to delete.')
      } finally { this.deleting = false }
    },

    async openCredentials(v) {
      this.credVisitor = v
      this.credentials = []
      this.credLoading = true
      this.credForm = { type: '', credential_number: '', issuing_country: '', expiry_date: '', notes: '' }
      try {
        const r = await axios.get(`visitors/profiles/${v.id}/credentials`)
        this.credentials = r.data.data ?? []
      } catch { this.toast.error('Failed to load credentials.') }
      finally { this.credLoading = false }
    },
    closeCredentials() { this.credVisitor = null; this.credentials = []; this.showCountryDrop = false; this.countryQuery = '' },
    async saveCred() {
      if (!this.credForm.type || !this.credForm.credential_number.trim()) {
        this.toast.error('Type and credential number are required.')
        return
      }
      this.credSaving = true
      try {
        const r = await axios.post(`visitors/profiles/${this.credVisitor.id}/credentials`, this.credForm)
        this.credentials.push(r.data.data)
        this.credForm = { type: '', credential_number: '', issuing_country: '', expiry_date: '', notes: '' }
        this.toast.success('Credential added.')
      } catch (e) {
        this.toast.error(e.response?.data?.message ?? 'Failed to add credential.')
      } finally { this.credSaving = false }
    },
    async deleteCred(c) {
      if (!confirm('Remove this credential?')) return
      try {
        await axios.delete(`visitors/profiles/${this.credVisitor.id}/credentials/${c.id}`)
        this.credentials = this.credentials.filter(x => x.id !== c.id)
        this.toast.success('Credential removed.')
      } catch (e) {
        this.toast.error(e.response?.data?.message ?? 'Failed to remove.')
      }
    },
    credTypeLabel(t) {
      return { passport: 'Passport', national_id: 'National ID', driving_license: 'Driving Licence', work_permit: 'Work Permit', other: 'Other' }[t] ?? t
    },
  },
}
</script>

<style scoped>
.page-header { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:12px; margin-bottom:20px; }
.page-title  { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.page-sub    { font-size:13px; color:#7a90b0; margin:0; }

.filter-bar { display:flex; align-items:center; gap:10px; flex-wrap:wrap; margin-bottom:16px; }
.filter-input  { padding:8px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; outline:none; font-family:inherit; }
.filter-input:focus { border-color:#004AAD; }
.filter-select { padding:8px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; outline:none; font-family:inherit; background:#fff; cursor:pointer; }
.per-page-wrap  { display:flex; align-items:center; gap:6px; }
.per-page-label { font-size:12px; color:#7a90b0; }
.per-page-select { padding:6px 10px; border:1px solid #dde5f0; border-radius:6px; font-size:13px; color:#1a2b45; font-family:inherit; }

.table-card { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }
.data-table { width:100%; border-collapse:collapse; }
.data-table th { padding:11px 14px; background:#f8fafc; font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.04em; color:#7a90b0; border-bottom:1px solid #e4eaf2; text-align:left; white-space:nowrap; }
.data-table td { padding:12px 14px; font-size:13px; color:#1a2b45; border-bottom:1px solid #f0f4f8; vertical-align:middle; }
.data-table tr:last-child td { border-bottom:none; }
.row--blocked td { background:#fef2f2; opacity:.85; }

.vis-cell { display:flex; align-items:center; gap:10px; }
.vis-avatar { width:34px; height:34px; border-radius:50%; background:linear-gradient(135deg,#004AAD,#1565d8); color:#fff; font-size:13px; font-weight:700; display:flex; align-items:center; justify-content:center; flex-shrink:0; overflow:hidden; }
.vis-avatar--blocked { background:linear-gradient(135deg,#dc2626,#ef4444); }
.vis-avatar-img { width:100%; height:100%; object-fit:cover; }
.vis-name { font-weight:600; font-size:13px; }
.vis-sub  { font-size:11px; color:#7a90b0; }

.chip { display:inline-flex; align-items:center; padding:3px 10px; border-radius:20px; font-size:11px; font-weight:600; }
.chip--green  { background:#dcfce7; color:#16a34a; }
.chip--red    { background:#fee2e2; color:#dc2626; }
.chip--amber  { background:#fef3c7; color:#d97706; }
.chip--blue   { background:#dbeafe; color:#1d4ed8; }

.btn-icon { background:none; border:none; cursor:pointer; color:#7a90b0; padding:5px 8px; border-radius:6px; font-size:14px; }
.btn-icon:hover { background:#f0f4f8; color:#004AAD; }
.btn-icon--red:hover   { background:#fef2f2; color:#dc2626; }
.btn-icon--green:hover { background:#dcfce7; color:#16a34a; }

.row-menu-wrap { position:relative; display:inline-block; }
.row-menu-trigger { background:none; border:none; cursor:pointer; color:#aab8cc; padding:5px 8px; border-radius:6px; font-size:15px; line-height:1; transition:background .1s,color .1s; }
.row-menu-trigger:hover { background:#f0f4f8; color:#3d5068; }
.row-menu-dropdown {
  position:fixed; z-index:9999;
  background:#fff; border:1px solid #e4eaf2; border-radius:10px;
  box-shadow:0 8px 24px rgba(0,0,0,.12); min-width:170px; overflow:hidden;
  padding:4px 0;
}
.row-menu-item {
  display:flex; align-items:center; gap:9px; width:100%; padding:9px 14px;
  background:none; border:none; cursor:pointer; font-size:13px; font-weight:500;
  color:#1a2b45; font-family:inherit; text-align:left; transition:background .1s;
  white-space:nowrap;
}
.row-menu-item:hover          { background:#f4f7fb; }
.row-menu-item--red           { color:#dc2626; }
.row-menu-item--red:hover     { background:#fff5f5; }
.row-menu-item--green         { color:#16a34a; }
.row-menu-item--green:hover   { background:#f0fdf4; }
.row-menu-icon { width:14px; opacity:.75; flex-shrink:0; }
.row-menu-divider { height:1px; background:#f0f4f8; margin:4px 0; }
.menu-pop-enter-active { transition:opacity .1s ease, transform .1s ease; }
.menu-pop-leave-active { transition:opacity .08s ease, transform .08s ease; }
.menu-pop-enter-from, .menu-pop-leave-to { opacity:0; transform:translateY(-4px) scale(.97); }

.btn-primary-action { display:inline-flex; align-items:center; gap:6px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:0 18px; height:38px; border-radius:8px; border:none; cursor:pointer; font-family:inherit; }
.btn-primary-action:hover:not(:disabled) { background:#003b8e; }
.btn-primary-action:disabled { opacity:.6; cursor:not-allowed; }
.btn-ghost { display:inline-flex; align-items:center; gap:6px; background:#f4f7fb; color:#3d5068; border:1px solid #e4eaf2; font-size:13px; font-weight:600; padding:0 18px; height:38px; border-radius:8px; cursor:pointer; font-family:inherit; }
.btn-ghost:hover:not(:disabled) { background:#e4eaf2; }
.btn-danger { display:inline-flex; align-items:center; gap:6px; background:#dc2626; color:#fff; font-size:13px; font-weight:600; padding:0 18px; height:38px; border-radius:8px; border:none; cursor:pointer; font-family:inherit; }
.btn-danger:hover:not(:disabled) { background:#b91c1c; }
.btn-danger:disabled { opacity:.6; cursor:not-allowed; }

.modal-overlay { position:fixed; inset:0; background:rgba(0,0,0,.45); z-index:1000; display:flex; align-items:center; justify-content:center; padding:16px; }
.modal-box    { background:#fff; border-radius:14px; width:100%; max-width:560px; box-shadow:0 24px 60px rgba(0,0,0,.2); }
.modal-head   { display:flex; align-items:center; justify-content:space-between; padding:18px 22px; border-bottom:1px solid #f0f4f8; }
.modal-title  { font-size:15px; font-weight:700; color:#1a2b45; margin:0; }
.modal-close  { background:none; border:none; cursor:pointer; color:#7a90b0; font-size:16px; padding:4px; }
.modal-body   { padding:20px 22px; display:flex; flex-direction:column; gap:12px; }
.modal-footer { display:flex; justify-content:flex-end; gap:10px; padding:16px 22px; border-top:1px solid #f0f4f8; }

.form-cols { display:grid; grid-template-columns:1fr 1fr; gap:12px; }
.form-row  { display:flex; flex-direction:column; gap:5px; }
.form-label { font-size:12px; font-weight:600; color:#4a5e78; }
.form-input { width:100%; padding:9px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; outline:none; box-sizing:border-box; font-family:inherit; }
.form-input:focus { border-color:#004AAD; }

.setting-row { display:flex; justify-content:space-between; align-items:center; gap:16px; padding:4px 0; }
.toggle-info { display:flex; flex-direction:column; gap:3px; }
.toggle-name { font-size:13px; font-weight:600; color:#1a2b45; }
.toggle-hint { font-size:12px; color:#7a90b0; }
.toggle-switch { position:relative; display:inline-block; width:38px; height:22px; flex-shrink:0; }
.toggle-switch input { opacity:0; width:0; height:0; }
.toggle-track  { position:absolute; cursor:pointer; inset:0; background:#dde5f0; border-radius:22px; transition:.2s; }
.toggle-track::before { position:absolute; content:''; height:16px; width:16px; left:3px; top:3px; background:#fff; border-radius:50%; transition:.2s; }
.toggle-switch input:checked + .toggle-track { background:#004AAD; }
.toggle-switch input:checked + .toggle-track::before { transform:translateX(16px); }

.pagination-bar { display:flex; align-items:center; justify-content:space-between; padding:12px 16px; border-top:1px solid #f0f4f8; flex-wrap:wrap; gap:8px; }
.pg-info   { font-size:12px; color:#7a90b0; }
.pg-controls { display:flex; gap:4px; }
.pg-btn    { padding:4px 10px; border:1px solid #e4eaf2; background:#f8fafc; border-radius:6px; font-size:12px; color:#3d5068; cursor:pointer; }
.pg-btn:disabled { opacity:.4; cursor:not-allowed; }
.pg-num    { padding:4px 10px; border:1px solid #e4eaf2; background:#fff; border-radius:6px; font-size:12px; color:#3d5068; cursor:pointer; }
.pg-num--active { background:#004AAD; color:#fff; border-color:#004AAD; }

.empty-state { text-align:center; padding:60px 20px; color:#aab8cc; }
.empty-icon  { font-size:40px; margin-bottom:12px; }

.sk-table { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }
.sk-row   { height:52px; background:linear-gradient(90deg,#f0f4f8 25%,#e4eaf2 50%,#f0f4f8 75%); background-size:200% 100%; animation:shimmer 1.4s infinite; border-bottom:1px solid #e4eaf2; }
@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }

/* Credentials */
.cred-list { display:flex; flex-direction:column; gap:8px; margin-bottom:16px; }
.cred-item { display:flex; align-items:flex-start; justify-content:space-between; gap:12px; background:#f8fafc; border:1px solid #e4eaf2; border-radius:10px; padding:12px 14px; }
.cred-item--expired { background:#fff7ed; border-color:#fed7aa; }
.cred-item-info { display:flex; flex-direction:column; gap:3px; flex:1; }
.cred-type-badge { display:inline-block; font-size:10px; font-weight:700; text-transform:uppercase; letter-spacing:.07em; background:#eff6ff; color:#1d4ed8; padding:2px 8px; border-radius:20px; width:fit-content; }
.cred-number { font-size:14px; font-weight:700; color:#1a2b45; letter-spacing:.03em; }
.cred-meta { font-size:12px; color:#7a90b0; }
.cred-notes { font-style:italic; }
.cred-expired-text { color:#c2410c; font-weight:600; }
.cred-add-section { border-top:1px solid #e4eaf2; padding-top:16px; margin-top:4px; }
.cred-form-grid { display:grid; grid-template-columns:1fr 1fr; gap:12px; }
@media(max-width:520px){ .cred-form-grid { grid-template-columns:1fr; } }
.section-label { font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.04em; color:#7a90b0; margin-bottom:14px; }

/* ── Country select ─────────────────────────────────────────────── */
.cs-wrap { position:relative; }
.cs-trigger {
  display:flex; align-items:center; justify-content:space-between; gap:8px;
  width:100%; padding:9px 12px; border:1px solid #dde5f0; border-radius:8px;
  font-size:13px; color:#1a2b45; background:#fff; cursor:pointer;
  box-sizing:border-box; transition:border-color .15s;
}
.cs-trigger:hover, .cs-trigger--open { border-color:#004AAD; }
.cs-selected { display:flex; align-items:center; gap:6px; }
.cs-placeholder { color:#aab8cc; }
.cs-flag { font-size:16px; line-height:1; }
.cs-chevron { font-size:11px; color:#aab8cc; flex-shrink:0; }
.cs-dropdown {
  position:absolute; top:calc(100% + 4px); left:0; right:0; z-index:500;
  background:#fff; border:1px solid #dde5f0; border-radius:10px;
  box-shadow:0 8px 24px rgba(0,0,0,.12); overflow:hidden;
}
.cs-search-wrap {
  display:flex; align-items:center; gap:8px; padding:10px 12px;
  border-bottom:1px solid #f0f4f8;
}
.cs-search-icon { color:#aab8cc; font-size:12px; flex-shrink:0; }
.cs-search-input {
  flex:1; border:none; outline:none; font-size:13px; color:#1a2b45;
  font-family:inherit; background:transparent;
}
.cs-clear { background:none; border:none; cursor:pointer; color:#aab8cc; padding:0; font-size:12px; flex-shrink:0; }
.cs-clear:hover { color:#3d5068; }
.cs-list { list-style:none; margin:0; padding:4px 0; max-height:220px; overflow-y:auto; }
.cs-item {
  display:flex; align-items:center; gap:8px; padding:8px 12px;
  cursor:pointer; font-size:13px; transition:background .1s;
}
.cs-item:hover { background:#f4f7fb; }
.cs-item--active { background:#eff6ff; }
.cs-name { flex:1; color:#1a2b45; }
.cs-code { font-size:11px; color:#aab8cc; font-weight:600; }
.cs-empty { padding:14px 12px; font-size:13px; color:#aab8cc; text-align:center; }
.cs-footer { padding:8px 12px; border-top:1px solid #f0f4f8; }
.cs-clear-btn {
  background:none; border:none; cursor:pointer; font-size:12px; color:#7a90b0;
  padding:0; font-family:inherit; font-weight:600;
}
.cs-clear-btn:hover { color:#dc2626; }
</style>
